/**
 * Cliente API de Pórtico — REST + SSE
 * Ref: 02-contrato-descriptor-sesion.md §2 (endpoints v1)
 *
 * ÚNICO punto de cambio mock -> real (R-PT-02). Cuando VITE_USE_MOCK=true
 * todas las llamadas resuelven contra el mock local. En modo real habla con
 * el kernel (VITE_API_BASE; en dev, el proxy /v1 de vite.config.js) con
 * Authorization: Bearer <ID token de Cognito> en TODAS las llamadas
 * (R-PT-04/05) — el token lo aporta src/auth/cognito.js, nunca el caller.
 */

import { MOCK_DESCRIPTOR } from '../mock/descriptor.js'
import { getMockTurn }     from '../mock/sse-events.js'
import { getBearerToken }  from '../auth/cognito.js'
import { ApiError }        from './errors.js'
import {
  listMockUsers,
  inviteMockUser,
  changeMockUserRole,
} from '../mock/admin-users.js'

const USE_MOCK = import.meta.env.VITE_USE_MOCK !== 'false'
export const isMock = USE_MOCK

// Identidad que el mock delegará en el bootstrap (tenant + usuario): permite
// al Login previsualizar marca e identidad ANTES del bootstrap SOLO en modo
// mock — data-driven desde el descriptor, sin ramas por tenant (US-58). En
// modo real es null: antes de entrar no hay tenant conocido.
export const mockLoginPreview = USE_MOCK
  ? { tenant: MOCK_DESCRIPTOR.tenant, user: MOCK_DESCRIPTOR.user }
  : null
// Producción: https://api.dktec.dkagora.ai/v1 (.env.production).
// Dev sin .env.local: '/v1' -> proxy de vite hacia el kernel local.
const API_BASE = import.meta.env.VITE_API_BASE || '/v1'

// ---------------------------------------------------------------------------
// Helpers internos
// ---------------------------------------------------------------------------

// Sesión caducada (401 del kernel): el cliente no decide nada (R-PT-01),
// solo avisa a SessionContext para forzar el re-login.
let onSessionExpired = null
export function setSessionExpiredHandler(fn) {
  onSessionExpired = fn
}

function authHeaders(json = true) {
  const h = {}
  if (json) h['Content-Type'] = 'application/json'
  const token = getBearerToken()
  if (token) h['Authorization'] = `Bearer ${token}`
  return h
}

// Único tratamiento del 401 (contrato §4: access 60 min, idle 30 min,
// absoluta 8 h): tirar la sesión local y obligar a volver a entrar.
function checkAuth(res, op) {
  if (res.status === 401) {
    onSessionExpired?.()
    throw new Error(`${op}: la sesión ha caducado (401)`)
  }
  return res
}

// Construye un ApiError con el `code` estable del envelope de error OBJETIVO del
// kernel ({ error: { code, message } }). Hoy el kernel real aún devuelve string
// plano, así que si el cuerpo no trae `code` se deriva uno del status
// (403 -> forbidden) para que la UI pueda mapearlo igualmente. Al migrar el
// kernel al envelope estructurado, este helper ya lo consume sin cambios.
async function apiError(res, op) {
  let code = null
  let message = `${op} falló (${res.status})`
  try {
    const body = await res.json()
    if (body?.error) {
      code = body.error.code ?? null
      message = body.error.message ?? message
    }
  } catch { /* respuesta sin cuerpo JSON */ }
  if (!code) {
    if (res.status === 403) code = 'forbidden'
    else if (res.status === 404 && op === 'approveAction') code = 'accion_no_encontrada'
    else code = 'unknown_error'
  }
  return new ApiError(code, message, res.status)
}

/**
 * Simula un stream SSE emitiendo los eventos del mock uno a uno con un
 * pequeño retardo entre ellos, llamando a onEvent para cada uno.
 * Llama a onDone cuando el stream termina.
 */
async function streamMockEvents(events, { onEvent, onDone, onError }) {
  try {
    for (const event of events) {
      await new Promise(r => setTimeout(r, event.type === 'progress' ? 600 : 300))
      onEvent(event)
      if (event.type === 'done') break
    }
    onDone()
  } catch (err) {
    onError(err)
  }
}

// ---------------------------------------------------------------------------
// POST /v1/session/bootstrap → descriptor (proyección segura)
// R-PT-01: sin lógica de negocio. R-PT-03: solo proyección segura.
// Real (contrato §3): Bearer en cabecera y SIN credenciales en el body —
// la identidad viene de Cognito, el kernel resuelve rol y capacidades.
// ---------------------------------------------------------------------------
export async function bootstrap() {
  if (USE_MOCK) {
    // Simular latencia de red
    await new Promise(r => setTimeout(r, 400))
    return {
      token: 'mock-bearer-token-001',
      descriptor: MOCK_DESCRIPTOR,
    }
  }

  const res = checkAuth(
    await fetch(`${API_BASE}/session/bootstrap`, {
      method: 'POST',
      headers: authHeaders(),
    }),
    'bootstrap',
  )
  if (!res.ok) throw new Error(`Bootstrap falló: ${res.status}`)
  const descriptor = await res.json()
  return { token: getBearerToken(), descriptor }
}

// ---------------------------------------------------------------------------
// GET /v1/chats/{chat_id} → historial paginado (R-PT-06, B2b)
// El kernel devuelve páginas de `turnos` ({seq, mensaje_usuario, respuesta,
// created_at}) con `next_cursor`; aquí se aplanan a la lista de mensajes que
// pinta ChatWindow (dos burbujas por turno). 404 = chat aún sin turnos
// persistidos (o ajeno) -> historial vacío.
// ---------------------------------------------------------------------------
export async function getChatHistory(chatId) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    // Devuelve conversación de muestra según el chat_id
    return {
      chat_id: chatId,
      messages: MOCK_CHAT_HISTORY[chatId] ?? [],
    }
  }

  const turnos = []
  let cursor = null
  do {
    const q = new URLSearchParams({ limit: '100' })
    if (cursor) q.set('cursor', cursor)
    const res = checkAuth(
      await fetch(`${API_BASE}/chats/${encodeURIComponent(chatId)}?${q}`, {
        headers: authHeaders(false),
      }),
      'getChatHistory',
    )
    if (res.status === 404) return { chat_id: chatId, messages: [] }
    if (!res.ok) throw new Error(`getChatHistory falló: ${res.status}`)
    const page = await res.json()
    turnos.push(...page.turnos)
    cursor = page.next_cursor
  } while (cursor && turnos.length < 2000)

  const messages = turnos.flatMap(t => [
    {
      id:        `t-${t.seq}-user`,
      role:      'user',
      content:   t.mensaje_usuario,
      timestamp: t.created_at,
    },
    {
      id:        `t-${t.seq}-agent`,
      role:      'agent',
      // US-02: el historial refleja a quién se dirigió el turno. El kernel
      // devuelve el id del agente destino (default 'anne' en turnos previos).
      agent:     { id: t.agente_destino ?? 'anne' },
      type:      'answer',
      payload:   { markdown: t.respuesta },
      timestamp: t.created_at,
    },
  ])
  // El agente_destino del último turno es el interlocutor actual del hilo (US-02):
  // permite reflejar en cabecera con quién se está hablando al reabrir.
  const threadAgent = turnos.length ? turnos[turnos.length - 1].agente_destino : null
  return { chat_id: chatId, messages, agente_destino: threadAgent ?? null }
}

// ---------------------------------------------------------------------------
// POST /v1/chats/{chat_id}/messages → stream SSE del turno (R-PT-07)
// Body { texto, agente_destino? } — el kernel rechaza campos extra. US-02:
// `agente_destino` dirige el turno a un agente concreto; si se omite, el
// kernel enruta por defecto a Anne-en-cliente (US-03).
// Parser SSE sobre fetch: EventSource no soporta POST ni Authorization.
// ---------------------------------------------------------------------------
export function sendMessage(chatId, content, { onEvent, onDone, onError, agenteDestino } = {}) {
  if (USE_MOCK) {
    const events = getMockTurn(content)
    streamMockEvents(events, { onEvent, onDone, onError })
    // Devuelve un cancelador no-op (en real sería controller.abort())
    return { cancel: () => {} }
  }

  // Solo se envía la clave cuando hay destino explícito: así el default de
  // Anne lo decide el kernel (no el front) y el body sigue siendo { texto }
  // en el path por defecto (US-03).
  const body = agenteDestino ? { texto: content, agente_destino: agenteDestino } : { texto: content }

  const controller = new AbortController()
  fetch(`${API_BASE}/chats/${encodeURIComponent(chatId)}/messages`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
    signal: controller.signal,
  }).then(async res => {
    checkAuth(res, 'sendMessage')
    if (!res.ok) throw new Error(`sendMessage falló: ${res.status}`)
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop()
      for (const chunk of lines) {
        const dataLine = chunk.split('\n').find(l => l.startsWith('data:'))
        if (dataLine) {
          try {
            onEvent(JSON.parse(dataLine.slice(5)))
          } catch { /* ignorar líneas mal formadas */ }
        }
      }
    }
    onDone()
  }).catch(err => {
    if (err.name !== 'AbortError') onError(err)
  })

  return { cancel: () => controller.abort() }
}

// ---------------------------------------------------------------------------
// POST /v1/actions/{action_id}/approve — R-PT-11
// ---------------------------------------------------------------------------
export async function approveAction(actionId) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 300))
    return {
      action_id: actionId,
      estado: 'ejecutada',
      resultado: 'Acción ejecutada (mock).',
    }
  }

  const res = checkAuth(
    await fetch(`${API_BASE}/actions/${encodeURIComponent(actionId)}/approve`, {
      method: 'POST',
      headers: authHeaders(false),
    }),
    'approveAction',
  )
  // SPEC #280 §B.4: propagar el `code` del envelope de error del kernel
  // ({ error: { code, message } }) para que la UI mapee 403/404/409 a un
  // mensaje estable, en vez de un Error genérico.
  if (!res.ok) throw await apiError(res, 'approveAction')
  return res.json()
}

// ===========================================================================
// Administración de usuarios y roles — US-26
// Contrato admin PROPUESTO en US-26 (endpoints /v1/admin/users): pendiente de
// VoBo de Cassie (es su contrato) y de los endpoints reales del kernel. NO es
// un acuerdo cerrado todavía. Hoy el kernel real no expone estas rutas ni el
// rol/scope admin; esta capa vive contra mock hasta la convergencia.
// Aislamiento por tenant y validación de permiso admin -> en el KERNEL; el
// front solo pinta y mapea errores (R-PT-01/R-PT-04). En cada endpoint el
// backend debe responder 403 si el usuario no es admin.
// ---------------------------------------------------------------------------
// GET /v1/admin/users → { users: [{ id, display_name, email, rol, estado }] }
// ---------------------------------------------------------------------------
export async function listUsers() {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 300))
    return { users: listMockUsers() }
  }
  const res = checkAuth(
    await fetch(`${API_BASE}/admin/users`, { headers: authHeaders(false) }),
    'listUsers',
  )
  if (!res.ok) throw await apiError(res, 'listUsers')
  return res.json()
}

// ---------------------------------------------------------------------------
// POST /v1/admin/users  body { email, rol } → 201 { user } (estado "pendiente")
// Invitar = AdminCreateUser (Cognito) + email de invitación (SES) en el kernel.
// Errores: 403 forbidden · 409 email_already_exists · 422 invalid_*
// ---------------------------------------------------------------------------
export async function inviteUser({ email, rol }) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 400))
    return { user: inviteMockUser({ email, rol }) }
  }
  const res = checkAuth(
    await fetch(`${API_BASE}/admin/users`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ email, rol }),
    }),
    'inviteUser',
  )
  if (!res.ok) throw await apiError(res, 'inviteUser')
  return res.json()
}

// ---------------------------------------------------------------------------
// PATCH /v1/admin/users/{user_id}  body { rol } → 200 { user }
// Cambiar rol = grupos Cognito en el kernel. Errores: 403 · 404 · 422.
// ---------------------------------------------------------------------------
export async function changeUserRole(userId, rol) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 300))
    return { user: changeMockUserRole(userId, rol) }
  }
  const res = checkAuth(
    await fetch(`${API_BASE}/admin/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ rol }),
    }),
    'changeUserRole',
  )
  if (!res.ok) throw await apiError(res, 'changeUserRole')
  return res.json()
}

// ---------------------------------------------------------------------------
// Mock de historiales de chat
// ---------------------------------------------------------------------------
const MOCK_CHAT_HISTORY = {
  'chat-001': [
    {
      id: 'h-001-1',
      role: 'user',
      content: '¿En qué estado está el contrato del descriptor de sesión?',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000 - 5000).toISOString(),
    },
    {
      id: 'h-001-2',
      role: 'agent',
      agent: { nombre: 'Cassie' },
      type: 'answer',
      payload: {
        markdown: 'El contrato del descriptor de sesión está en **v1.0**, congelado desde el 2026-06-25. Incluye el esquema de bootstrap (§3), la política de sesión (§4) y el contrato de eventos SSE (§5). Listo para construir contra mock.',
      },
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    },
  ],
  'chat-002': [
    {
      id: 'h-002-1',
      role: 'user',
      content: '¿Cuál es el plan para el onboarding de Koana?',
      timestamp: new Date(Date.now() - 25 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'h-002-2',
      role: 'agent',
      agent: { nombre: 'Cassie' },
      type: 'answer',
      payload: {
        markdown: 'El onboarding de Koana sigue el track de proyecto ORIGEN. El primer paso es preparar el proyecto ORIGEN para recibirlos — milestone F040. Las primeras acciones de colaboración en KOANA arrancan en F050.',
      },
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    },
  ],
  'chat-003': [],
}
